/**
 * useAgentDrawerData - Data fetching hook for AgentDrawer
 *
 * Uses a fallback pattern since there is no per-agent endpoint.
 * The backend returns all agents from /agent/enhanced, and we filter
 * by client_id on the client side.
 *
 * For Phase 1, this is a simple query. Phase 2 will add prefetch support.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { Agent } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';

export function useAgentDrawerData(agentId: string | null) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useQuery<Agent | null>({
    queryKey: ['agent', agentId, 'enhanced'],
    queryFn: async () => {
      if (!agentId) return null;

      // Fallback: Filter from full list (backend has no per-agent endpoint)
      // This is acceptable for 10-15 agents (~0.5-1.5s total)
      const response = await axios.get<Agent[]>('/agent/enhanced');
      const agent = response.data.find(a => a.client_id === agentId);

      if (!agent) {
        throw new Error(`Agent ${agentId} not found`);
      }

      return agent;
    },
    // Try to use prefetched data from global state (Phase 2 will enhance this)
    initialData: () => {
      return queryClient.getQueryData<Agent>(['agent', agentId, 'enhanced']);
    },
    initialDataUpdatedAt: () => {
      return queryClient.getQueryState(['agent', agentId, 'enhanced'])
        ?.dataUpdatedAt;
    },
    enabled: agentId !== null,
    staleTime: 60_000, // 1 minute
    gcTime: 300_000, // 5 minutes
    retry: 1,
  });
}
